import React, { useEffect, useState, useRef } from "react";
import useAxios from "../../../utils/useAxios";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SelectInput from "../../../components/SelectInput";
import { toast } from "react-toastify";
import FeatherIcon from "feather-icons-react";
import {Block, TEAM_SIZE_OPTIONS} from "../../../constants";
import ErrorMessage from "../../../components/ErrorMessage";
import useSubmitLock from "../../../hooks/useSubmitLock";
import TournamentBlockToggle from "../TournamentBlockToggle";
import PageLoader from "../../../components/PageLoader";

const PLAYERS_COUNT_OPTIONS = Array.from({ length: 127 }, (_, i) => i + 2)
    .map(v => ({ id: v, name: String(v) }));

const TournamentPlayersSettings = ({ tournamentId, tournamentBlocks, setTournament: setGlobalTournament, onClose, onSaved, onReset }) => {
    const api = useAxios();
    const { t } = useTranslation();
    const { id: routeId } = useParams();
    const id = tournamentId || routeId;
    const { isSubmitting, wrapSubmit } = useSubmitLock();

    const [tournament, setTournament] = useState(null);
    const [playersCount, setPlayersCount] = useState(0);
    const [bases, setBases] = useState([]);
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    // Guards fetchData against out-of-order responses: the mount effect
    // below fires one on load, and handleReset fires another right after
    // resetting - without this, a slower pre-reset response can land after
    // the reset's and silently overwrite the freshly-reset state with the
    // old one.
    const dataReqRef = useRef(0);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchData = async () => {
        setLoading(true);
        dataReqRef.current += 1;
        const currentReq = dataReqRef.current;
        try {
            const tournamentRes = await api.get(`/api/v1.0/tournaments/${id}/`);
            if (dataReqRef.current !== currentReq) return;
            const data = tournamentRes.data;
            setTournament(data);

            setSettings({
                player_base: data.player_base,
                player_quantity: data.player_quantity,
                team_size: data.team_size || null
            });

            const countUrl = data.grade > 1
                ? `/api/v1.0/tournaments/${id}/teams/?limit=1`
                : `/api/v1.0/tournaments/${id}/players/?limit=1`;
            const countRes = await api.get(countUrl);
            if (dataReqRef.current !== currentReq) return;
            setPlayersCount(countRes.data.count || 0);

            await fetchLists();
        } catch (e) {
            console.error(e);
            toast.error(t("Failed to load settings"));
        } finally {
            setLoading(false);
        }
    };

    const fetchLists = async () => {
        try {
            const basesRes = await api.get("/api/v1.0/players/base/");

            setBases(
                (basesRes.data.results || []).map(b => ({
                    id: b.id,
                    name: b.name,
                    write: b.write
                }))
            );
        } catch {
            toast.error(t("Failed to load settings"));
        }
    };

    const handleSave = wrapSubmit(async () => {
        if (!settings) return;

        setErrors({});

        if (!settings.player_base) {
            setErrors({ player_base: "Players base is required" });
            return;
        }

        setLoading(true);
        try {
            await api.patch(`/api/v1.0/tournaments/${id}/`, {
                player_base: settings.player_base,
                player_quantity: settings.player_quantity,
                team_size: settings.team_size,
            });

            onSaved?.();
        } catch {
            toast.error(t("Failed to save settings"));
        } finally {
            setLoading(false);
        }
    });

    const handleReset = wrapSubmit(async () => {
        if (!window.confirm(t("Are you sure you want to reset?"))) return;

        setLoading(true);
        try {
            await api.post(`/api/v1.0/tournaments/${id}/players/reset/`);
            // Re-fetch the authoritative post-reset state instead of
            // leaving this form's own `settings`/`playersCount` showing
            // whatever was there before the reset.
            await fetchData();

            onReset ? onReset() : onSaved?.();
        } catch {
            toast.error(t("Failed to reset tournament"));
        } finally {
            setLoading(false);
        }
    });

    if (loading && !settings) {
        return <PageLoader />;
    }

    if (!tournament || !settings) {
        return <PageLoader />;
    }

    if (!tournament.grade) {
        return (
            <div className="empty">
                <center>
                    <FeatherIcon icon="info"/>
                    <p>{t("Set the tournament grade on the Information tab first")}</p>
                </center>
            </div>
        );
    }

    const minPlayerQuantity = playersCount || 0;

    const minTeamSize = tournament.grade === 3
        ? Math.max(...(tournament.teams || []).map(team => team.players?.length || 0), 0)
        : 0;

    const playerQuantityOptions = PLAYERS_COUNT_OPTIONS.filter(
        option => option.id >= minPlayerQuantity
    );

    const teamSizeOptions = TEAM_SIZE_OPTIONS.filter(
        option => option.id >= minTeamSize
    );

    return (
        <form>
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                <TournamentBlockToggle
                    tournamentId={id}
                    tournamentBlocks={tournamentBlocks}
                    setTournament={setGlobalTournament}
                    blockType={Block.PARTICIPANTS}
                />
            </div>

            <SelectInput
                data={bases}
                label_text={t("Players base")}
                default_text={t("Choose players base")}
                input_name="player_base"
                default_value={settings.player_base}
                setInputValue={(id) =>
                    setSettings(prev => ({ ...prev, player_base: id }))
                }
                canWrite={!playersCount}
                onBlockedClickText={t("You cannot change the players base because players were already added")}
            />
            <div style={{position: "relative", marginTop: "-10px"}}>
                <ErrorMessage error={errors.player_base && t(errors.player_base)}/>
            </div>

            <SelectInput
                data={playerQuantityOptions}
                label_text={tournament.grade === 3 ? t("Number of teams") : tournament.grade === 2
                    ? t("Doubles quantity")
                    : t("Players quantity")}
                default_text={tournament.grade === 3 ? t("Choose number of teams") : tournament.grade === 2
                    ? t("Choose doubles quantity")
                    : t("Choose players quantity")}
                input_name="player_quantity"
                default_value={settings.player_quantity}
                setInputValue={(id) =>
                    setSettings(prev => ({ ...prev, player_quantity: id }))
                }
            />

            {tournament.grade === 3 && (
                <SelectInput
                    data={teamSizeOptions}
                    label_text={t("Team size")}
                    default_text={t("Choose team size")}
                    input_name="team_size"
                    default_value={settings.team_size}
                    setInputValue={(id) =>
                        setSettings(prev => ({ ...prev, team_size: id }))
                    }
                />
            )}
            <ul className="tabs settings-form-actions">
                <li>
                    <a
                        href="#"
                        className={`action-button ${loading || isSubmitting ? "disabled" : ""}`}
                        onClick={loading || isSubmitting ? (e) => e.preventDefault() : handleSave}
                        aria-disabled={loading || isSubmitting}
                    >
                        <FeatherIcon icon="check" /> {t("Save")}
                    </a>
                </li>
                <li>
                    <a
                        href="#"
                        className={`action-button ${loading || isSubmitting ? "disabled" : ""}`}
                        onClick={loading || isSubmitting ? (e) => e.preventDefault() : handleReset}
                        aria-disabled={loading || isSubmitting}
                    >
                        <FeatherIcon icon="rotate-ccw" /> {t("Reset")}
                    </a>
                </li>
            </ul>
        </form>
    );
};

export default TournamentPlayersSettings;
